// Pure mapping from a Spatial Vision job item to the comment tags the viewer
// stores. Kept free of app imports so it runs under plain `node --test`.

const MAX_TAG_TITLE_LENGTH = 50;

export const numberOr = (value, fallback = 0) => {
  const numeric = Number(value);
  return Number.isFinite(numeric) ? numeric : fallback;
};

export function isPresent(value) {
  if (value === null || value === undefined) return false;
  if (typeof value === 'string') return value.trim() !== '';
  if (Array.isArray(value)) return value.length > 0;
  if (typeof value === 'object') return Object.keys(value).length > 0;
  return true;
}

export function printable(value) {
  if (Array.isArray(value)) {
    return value
      .map(item => printable(item))
      .filter(Boolean)
      .join(', ');
  }
  if (value && typeof value === 'object') {
    return JSON.stringify(value, null, 2);
  }
  return String(value);
}

// The PRO worker downsizes to a 1024 max side and reports boxes in that frame,
// while the viewer draws over the original upload. Gemini does not resize, so
// originalSize is absent there and the scale stays 1.
export function exportScale(item) {
  const metadata = item?.resultMetadata || item?.metadata || {};
  const preprocessing = metadata.preprocessing || {};
  const originalWidth = numberOr(preprocessing.originalSize?.width);
  const processedWidth = numberOr(
    metadata.imageWidth ?? preprocessing.processedSize?.width,
  );
  if (originalWidth > 0 && processedWidth > 0) {
    return originalWidth / processedWidth;
  }
  return 1;
}

function polygonRings(detection) {
  const mask = detection?.mask;
  if (mask?.type === 'polygon' && Array.isArray(mask.polygons)) {
    return mask.polygons;
  }
  if (Array.isArray(detection?.polygon)) return [detection.polygon];
  return [];
}

function normalizePoint(point) {
  if (Array.isArray(point) && point.length >= 2) {
    return { x: numberOr(point[0]), y: numberOr(point[1]) };
  }
  if (point && typeof point === 'object') {
    return { x: numberOr(point.x), y: numberOr(point.y) };
  }
  return null;
}

function bounds(points) {
  const xs = points.map(point => point.x);
  const ys = points.map(point => point.y);
  return {
    minX: Math.min(...xs),
    maxX: Math.max(...xs),
    minY: Math.min(...ys),
    maxY: Math.max(...ys),
  };
}

function largestRing(detection) {
  let best = null;
  let bestArea = -1;
  for (const ring of polygonRings(detection)) {
    if (!Array.isArray(ring)) continue;
    const points = ring.map(normalizePoint).filter(Boolean);
    if (points.length < 3) continue;
    const { minX, maxX, minY, maxY } = bounds(points);
    const area = (maxX - minX) * (maxY - minY);
    if (area > bestArea) {
      best = points;
      bestArea = area;
    }
  }
  return best;
}

function boxCorners(detection) {
  const box = detection?.bbox || {};
  const x = numberOr(box.x ?? detection?.coordX);
  const y = numberOr(box.y ?? detection?.coordY);
  const width = numberOr(box.w ?? detection?.width);
  const height = numberOr(box.h ?? detection?.height);
  if (width <= 0 && height <= 0) return null;
  return [
    { x, y },
    { x: x + width, y },
    { x: x + width, y: y + height },
    { x, y: y + height },
  ];
}

// `mask` is read by LabelTools.syncMasksFromTags, which expects {x, y} points.
export function automatedTagPosition(detection, scale = 1) {
  const points = largestRing(detection) || boxCorners(detection);
  if (!points) return null;
  const scaled = point => Math.round(point * scale);
  const { minX, maxX, minY, maxY } = bounds(points);
  return {
    x: scaled(minX + (maxX - minX) / 2),
    y: scaled(minY + (maxY - minY) / 2),
    z: 0,
    mask: points.map(point => ({ x: scaled(point.x), y: scaled(point.y) })),
  };
}

export function conditionResultsForItem(item) {
  return (
    item?.conditionResults ||
    item?.resultMetadata?.conditionResults ||
    item?.resultMetadata?.condition_results ||
    []
  ).filter(result => result && typeof result === 'object');
}

function conditionResultStatus(result) {
  return String(result?.resultStatus || result?.result_status || '')
    .replace(/[_-]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}

export function conditionResultEvidenceIndexes(result) {
  const indexes =
    result?.relatedDetectionIndexes || result?.related_detection_indexes || [];
  if (!Array.isArray(indexes)) return [];
  return indexes
    .map(index => Number(index))
    .filter(
      (index, position, values) =>
        Number.isInteger(index) &&
        index >= 0 &&
        values.indexOf(index) === position,
    );
}

export function tagTitle(detection, index) {
  const title =
    detection?.title ||
    detection?.label ||
    detection?.abnormalityType ||
    `Detection ${index + 1}`;
  return String(title).trim().slice(0, MAX_TAG_TITLE_LENGTH);
}

export function automatedTagText(detection, context = {}) {
  const analysis =
    detection?.analysis ||
    detection?.returnDescription ||
    detection?.description ||
    detection?.summary ||
    detection?.answer ||
    detection?.inspectionRationale ||
    context?.inspectionRationale ||
    context?.resultMetadata?.finalAnswer ||
    context?.llmOutput;

  return isPresent(analysis) ? printable(analysis) : '';
}

export function tagText(detection, context) {
  return automatedTagText(detection, context);
}

function overallSummaryLines(item) {
  const lines = [];
  const status =
    item?.inspectionStatus || item?.resultMetadata?.inspectionStatus;
  const answer =
    item?.llmOutput || item?.resultMetadata?.finalAnswer || item?.summary || '';
  const rationale =
    item?.inspectionRationale ||
    item?.resultMetadata?.inspectionRationale ||
    '';
  if (isPresent(status)) lines.push(`Overall status: ${printable(status)}`);
  if (isPresent(answer)) lines.push(`Overall answer: ${printable(answer)}`);
  if (isPresent(rationale) && rationale !== answer) {
    lines.push(`Overall rationale: ${printable(rationale)}`);
  }
  return lines;
}

export function conditionSummaryText(item) {
  const results = conditionResultsForItem(item);
  const overall = overallSummaryLines(item);
  if (!results.length && !overall.length) return '';

  const lines = ['Spatial Vision condition results', ...overall, ''];

  results.forEach((result, index) => {
    const title =
      result?.conditionName ||
      result?.condition_name ||
      result?.name ||
      `Condition ${index + 1}`;
    lines.push(`${index + 1}. ${title}`);
    if (isPresent(result?.mode)) lines.push(`   Mode: ${result.mode}`);
    if (isPresent(result?.statusLabel || result?.status_label)) {
      lines.push(
        `   Status label: ${result.statusLabel || result.status_label}`,
      );
    }
    if (isPresent(result?.resultStatus || result?.result_status)) {
      lines.push(`   Result: ${conditionResultStatus(result)}`);
    }
    if (isPresent(result?.answer)) {
      lines.push(`   Answer: ${printable(result.answer)}`);
    }
    if (isPresent(result?.rationale)) {
      lines.push(`   Rationale: ${printable(result.rationale)}`);
    }

    const evidence = conditionResultEvidenceIndexes(result)
      .map(evidenceIndex => {
        const detection = item?.detections?.[evidenceIndex];
        return detection
          ? tagTitle(detection, evidenceIndex)
          : `Box ${evidenceIndex + 1}`;
      })
      .filter(Boolean);
    lines.push(
      `   Evidence boxes: ${evidence.length ? evidence.join(', ') : 'None linked'}`,
    );
    lines.push('');
  });

  return lines.join('\n').trim();
}

export function hasExportableSpatialVisionContent(item) {
  if (!item) return false;
  return Boolean(
    (item.detections || []).length ||
    conditionResultsForItem(item).length ||
    conditionSummaryText(item),
  );
}
